/*
 * This service, `SessionTimeoutService`, is responsible for managing the client-side aspects of user session
 * inactivity and prompting the user before their server-side session potentially expires due to inactivity.
 *
 * It works in conjunction with the `AuthenticationService` to maintain and validate the overall session state.
 *
 * Key functionalities and interactions:
 *
 * 1.  **Inactivity Detection:**
 * - `user_timeout_ms`: Defines the total duration of inactivity (e.g., 2 minutes) after which the session *might* expire on the server if not refreshed.
 * - `user_warning_threshold_ms`: Defines how much time before `user_timeout_ms` a warning dialog should be shown (e.g., 60 seconds before timeout).
 * - `is_user_active`: A boolean flag that is set to `true` when the user interacts with the application (e.g., via a `HostListener` for mouse movements, though not explicitly shown in this snippet, it's implied this would be set by external events).
 *
 * 2.  **Session Sunset Timestamp (`_sunset_timestamp_epoch_ms` / `sunset_timestamp_epoch_ms` setter):**
 * - This property stores the exact epoch timestamp (in milliseconds) when the *server-side* session is expected to expire.
 * - The `AuthenticationService` is expected to fetch this `sunset_timestamp_epoch_ms` value from the `/service/session/get_authentication_state` endpoint and pass it to this service's `sunset_timestamp_epoch_ms` setter.
 * - **Crucially, whenever a new `sunset_timestamp_epoch_ms` is set:**
 * - Any existing `dialog_timeout` (which triggers the warning dialog) is cleared.
 * - A new `dialog_timeout` is scheduled to trigger the `show_inactive_dialog()` at `_sunset_timestamp_epoch_ms - Date.now() - this.user_warning_threshold_ms`.
 * - This ensures the client-side warning timer is always synchronized with the *server's* perceived session expiration time.
 * - It also includes logic to `close_inactive_dialog()` if the new sunset time indicates there's still a long time left, useful for multi-tab scenarios where one tab might be inactive while another refreshes the session.
 *
 * 3.  **User Interaction (`user_interacted()`):**
 * - This method is intended to be called when any user activity (mouse movement, keyboard input, etc.) is detected in the Angular application.
 * - It sets `is_user_active` to `true`, indicating the user is no longer idle.
 * - It immediately `close_inactive_dialog()` if it's open and clears the `dialog_timeout`.
 * - **Important:** While `user_interacted()` marks activity, it's the `AuthenticationService`'s periodic poll (which checks `session_service.is_user_active`) that will then send the `restart_session_timeout` signal to the server.
 *
 * 4.  **Refreshing Server Session (`reset_session_timer()`):**
 * - This method makes an HTTP POST request to `restart_session_timeout` endpoint on the server.
 * - **Interaction with `AuthenticationService`:** This method is called by the `AuthenticationService` when `AuthenticationService.is_user_active` is `true`. The `AuthenticationService` effectively checks this `SessionTimeoutService`'s `is_user_active` flag to decide when to tell the server to extend the session.
 * - The server is then expected to respond with an updated `sunset_timestamp_epoch_ms` (via `get_authentication_state`), which is then fed back into this service to reset the client-side warning timer.
 *
 * 5.  **Inactivity Warning Dialog:**
 * - `show_inactive_dialog()`: Opens the `SessionTimeoutDialog` component using `NbDialogService` when the calculated warning threshold is met. It prevents opening multiple dialogs.
 * - `close_inactive_dialog()`: Closes the active dialog.
 *
 * **Interaction Summary with `AuthenticationService`:**
 * The `SessionTimeoutService` and `AuthenticationService` have a cooperative relationship:
 * - `SessionTimeoutService` **detects client-side user activity** (`is_user_active`) and **manages the client-side inactivity warning timer/dialog**.
 * - `AuthenticationService` **periodically polls the server** for authentication state (using `validate_authentication()`).
 * - During its polling, `AuthenticationService` **checks `SessionTimeoutService.is_user_active`**. If `true`, it may instruct the server to refresh the session by calling `SessionTimeoutService.reset_session_timer()`.
 * - The server's response to `get_authentication_state` (which includes `sunset_timestamp_epoch_ms`) is then used by `AuthenticationService` to **update `SessionTimeoutService.sunset_timestamp_epoch_ms`**, effectively synchronizing and resetting the client-side warning timer.
 */
import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { NbDialogService } from '@nebular/theme'
import { environment } from '../../../../environments/environment'
import { SessionTimeoutDialog } from '../../../pages/session_timeout_dialog/session_timeout_dialog.component'


@Injectable( {
  providedIn: 'root',
} )
export class SessionTimeoutService {
  endpoint = environment.api_endpoint;
  user_timeout_ms = 2 * 60 * 1000; // 2 minutes
  remaining_time_s = this.user_timeout_ms / 1000;
  user_warning_threshold_ms = 60 * 1000; // Warn 1 minute before timeout
  dialog
  dialog_visible = false;
  is_user_active = true; // Set to true by user interaction (e.g., HostListener in App component)
  _sunset_timestamp_epoch_ms = Date.now() + this.user_timeout_ms; // Initial client-side sunset
  dialog_timeout

  constructor(
    private dialogService: NbDialogService,
    // private authentication_service: AuthenticationService, // This was commented out in your original code
    private http: HttpClient,
  ) {
    // Schedule the initial dialog warning based on the initial sunset time.
    // This timeout will be cleared and reset whenever `sunset_timestamp_epoch_ms` is updated.
    this.dialog_timeout = setTimeout( () => {
      this.show_inactive_dialog()
    }, this._sunset_timestamp_epoch_ms - Date.now() - this.user_warning_threshold_ms )
  }

  /**
   * Setter for the session's sunset timestamp. This is typically updated by the
   * AuthenticationService after a successful authentication state check.
   * It resets the client-side warning timer.
   * @param sunset_timestamp_epoch_ms The new session sunset timestamp in milliseconds since epoch.
   */
  set sunset_timestamp_epoch_ms ( sunset_timestamp_epoch_ms: number ) {
    if ( sunset_timestamp_epoch_ms ) {
      this._sunset_timestamp_epoch_ms = sunset_timestamp_epoch_ms
    }

    // Clear any previously scheduled dialog timeout to prevent multiple timers.
    clearTimeout( this.dialog_timeout )

    // Calculate the time until the warning dialog should appear.
    // This is the server's sunset time minus current time minus the warning threshold.
    const timeout_ms = this._sunset_timestamp_epoch_ms - Date.now() - this.user_warning_threshold_ms

    // If there's a long time until timeout, ensure the dialog is closed.
    // This is especially important for scenarios with multiple tabs where one tab
    // might have refreshed the session.
    if ( timeout_ms > this.user_warning_threshold_ms ) { // Adjusted condition for clarity
      this.close_inactive_dialog()
    }

    // Schedule the dialog to appear only if running in production and there's a valid timeout duration.
    if ( environment.production && timeout_ms > 0 ) {
      this.dialog_timeout = setTimeout( () => {
        this.show_inactive_dialog()
      }, timeout_ms )
    }
  }

  // The commented-out setTimer() method is left as-is, assuming it's intentionally disabled.
  // setTimer() {
  //   timer(0, 1000).pipe(
  //     // take(this.remaining_time_s),
  //     map(() => {
  //       if (this.remaining_time_s > 0) {
  //        --this.remaining_time_s;
  //       } else {
  //         if (this.authentication_service.is_logged_in) {
  //           this.authentication_service.logout();
  //         }
  //       }
  //       if (this.remaining_time_s <= 60) {
  //         this.show_inactive_dialog();
  //       }
  //     }),
  //   ).subscribe();
  // }

  /**
   * Displays the session timeout warning dialog.
   * Ensures only one dialog is visible at a time.
   */
  show_inactive_dialog () {
    if ( !this.dialog_visible ) {
      // Calculate the actual remaining time based on the server's sunset timestamp
      const remaining_time_s = Math.ceil( ( this._sunset_timestamp_epoch_ms - Date.now() ) / 1000 )

      this.dialog = this.dialogService.open( SessionTimeoutDialog, {
        context: {
          title: 'Your session is about to expire due to inactivity.',
          remaining_time_s: remaining_time_s, // Pass the correct remaining time to the dialog
        },
      } )
      this.dialog_visible = true
    }
  }

  /**
   * Call this method when the user interacts with the application (e.g., mouse move, key press).
   * It marks the user as active and closes any open warning dialog.
   */
  user_interacted () {
    this.is_user_active = true
    this.close_inactive_dialog()
    clearTimeout( this.dialog_timeout ) // Clear the warning timeout immediately on interaction
    // The AuthenticationService's next poll will then use `is_user_active` to call `restart_session_timeout` on the server.
  }

  /**
   * Sends a request to the server to reset the session timeout.
   * This method is typically called by the AuthenticationService when user activity is detected.
   * @returns An Observable for the HTTP POST request to restart the session timeout.
   */
  reset_session_timer () {
    return this.http.post( `${this.endpoint}/service/session/restart_session_timeout`, {} )
  }

  /**
   * Closes the session timeout warning dialog if it's currently open.
   */
  close_inactive_dialog () {
    if ( this.dialog_visible && this.dialog ) {
      this.dialog_visible = false
      this.dialog.close()
    }
  }
}
